<template>
    <div class="quiz-container">
      <h2 class="quiz-title">{{ quizTitle }}</h2>
      <div v-if="!showSummary">
    
        <p class="question">{{ currentQuestion.beforeText }} <strong>_______</strong> {{ currentQuestion.afterText }}</p>
        <div class="options-container">
          <button
            v-for="option in currentQuestion.options"
            :key="option"
            :class="{'option-button': true, 'correct': option === currentQuestion.correctAnswer && feedbackVisible, 'incorrect': option !== currentQuestion.correctAnswer && option === selectedOption && feedbackVisible}"
            @click="selectOption(option)"
          >
            {{ option }}
          </button>
      
        </div>
        <audio ref="correctSound" src="https://assets.codepen.io/11151897/nyse.m4a"></audio>
        <div class="feedback" v-if="feedbackVisible">
          <span :class="{ 'correct': isAnswerCorrect, 'incorrect': !isAnswerCorrect }">{{ feedbackMessage }}</span>
        </div>
      </div><br>
      <button class="hint-button" @click="openHint(currentQuestion.hint)">
      <i class="fa fa-paw"></i> Hint
    </button>
      <div v-if="showSummary" class="summary-container">
        <h3>Quiz Summary</h3>
        <p>Correct Answers: {{ correctAnswers }} out of {{ totalQuestions }}</p>
        <button @click="restartQuiz" class="restart-quiz-button">Restart Quiz</button>
        <button @click="goToNextQuiz" class="restart-quiz-button">Next Quiz</button>
      </div>
    </div>
  </template>
  
  <script>
import { ref, computed, onMounted, watch } from 'vue'
  import Swal from 'sweetalert2';
  import axios from 'axios';
import { Auth } from 'aws-amplify';
  
  export default {
    setup() {
      const correctSound = ref(null);
      const quizTitle = ref("Foundational: Introduction to Financial Literacy");
      const questions = ref([
        {
          beforeText: "The practice of tracking and planning your income and expenses is known as",
          afterText: "management.",
          options: ["Budget", "Debt", "Investment", "Wealth"],
          correctAnswer: "Budget",
          hint: {
            text: "It's like planning how many treats you can buy and enjoy without running out before your next allowance!",
            imageUrl: require('@/assets/quizzes.png')
          }
        },
        {
          beforeText: "Saving a portion of your income regularly to use in the future is crucial for",
          afterText: "planning.",
          options: ["Emergency", "Vacation", "Luxury", "Retirement"],
          correctAnswer: "Emergency",
          hint: {
            text: "Imagine you suddenly need to visit the vet. It's important to have some savings set aside for unexpected situations!",
            imageUrl: require('@/assets/quizzes.png')
          }
        },
        {
          beforeText: "Buying stocks or bonds in order to earn income or profit over time involves",
          afterText: ".",
          options: ["Spending", "Investing", "Borrowing", "Lending"],
          correctAnswer: "Investing",
          hint: {
            text: "Think of it as planting a seed and waiting for it to grow into a tree that will bear fruits in the future.",
            imageUrl: require('@/assets/quizzes.png')
          }
        },
        {
          beforeText: "The use of borrowed money to purchase goods or services is known as",
          afterText: ".",
          options: ["Saving", "Credit", "Investment", "Insurance"],
          correctAnswer: "Credit",
          hint: {
            text: "Like getting a toy now and paying for it with your future allowances.",
            imageUrl: require('@/assets/quizzes.png')
          }
        },
        {
          beforeText: "Protecting yourself from financial losses due to unexpected events involves purchasing",
          afterText: ".",
          options: ["Stocks", "Bonds", "Insurance", "Mutual Funds"],
          correctAnswer: "Insurance",
          hint: {
            text: "It's like having a safety net in case you fall while climbing a tree.",
            imageUrl: require('@/assets/quizzes.png')
          }
        },
      ]);
      const currentQuestionIndex = ref(0);
      const correctAnswers = ref(0);
      const feedbackMessage = ref("");
      const feedbackVisible = ref(false);
      const isAnswerCorrect = ref(false);
      const showSummary = ref(false);
      const selectedOption = ref("");


      const totalQuestions = computed(() => questions.value.length);
      const openHint = (hint) => {
        Swal.fire({
          title: 'Hint',
          text: hint.text,
          imageUrl: hint.imageUrl,
          imageWidth: 400,
          imageHeight: 200,
          imageAlt: 'A hint image',
        });
      };
  
      const currentQuestion = computed(() => questions.value[currentQuestionIndex.value]);
  
      const selectOption = (option) => {
        selectedOption.value = option;
        isAnswerCorrect.value = option === currentQuestion.value.correctAnswer;
        feedbackMessage.value = isAnswerCorrect.value ? "Correct!" : "Incorrect!";
        feedbackVisible.value = true;
  
        if (isAnswerCorrect.value) {
          correctAnswers.value++;
          if (correctSound.value) {
            correctSound.value.play();
          }
        }
        setTimeout(() => {
          if (currentQuestionIndex.value < questions.value.length - 1) {
            currentQuestionIndex.value++;
            feedbackVisible.value = false;
          } else {
            showSummary.value = true;
          }
        }, 2000);
      };

      const submitQuizAttempt = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const cognitoSub = user.attributes.sub; // Retrieve Cognito sub

    const payload = {
      user: cognitoSub, // Include Cognito sub as the user identifier
      quiz: 'Exact Quiz Title', // Replace with the actual title of the quiz
      score: 75.5, // Example score
      correct_answers: 7, // Example number of correct answers
      incorrect_answers: 3, // Example number of incorrect answers
      attempt_date: new Date().toISOString(),
    };

    // Send quiz attempt data
    await axios.post('https://api.riskypaws.com/frontoffice/api/userquizzes/', payload);

    console.log('Quiz attempt submitted successfully');
  } catch (error) {
    console.error('Error submitting quiz attempt:', error.response ? error.response.data : error);
  }
};




      const restartQuiz = () => {
        currentQuestionIndex.value = 0;
        correctAnswers.value = 0;
        feedbackVisible.value = false;
        showSummary.value = false;
      };

      onMounted(() => {
      watch(showSummary, (newVal) => {
        if (newVal === true) {
          submitQuizAttempt();
        }
      });
    });
  
      return {
        quizTitle,
        currentQuestion,
        currentQuestionIndex,
        correctAnswers,
        selectOption,
        restartQuiz,
        showSummary,
        feedbackMessage,
        feedbackVisible,
        isAnswerCorrect,
        correctSound,
        selectedOption,
        totalQuestions,
        openHint,
      };
    },
  };
  </script>



<style scoped>
.quiz-container {
  color: #FFF;
  padding: 20px;
  border-radius: 10px;
  max-width: 600px;
  margin: auto;
  background: linear-gradient(135deg, #6D5BBA 0%, #8D58BF 100%);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.question-container {
  margin-bottom: 20px;
}

.question {
  font-size: 1.25rem;
  margin-bottom: 15px;
}

.options-container, .options-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  list-style-type: none;
  padding: 0;
}

.option-button, .option {
  padding: 10px 20px;
  border-radius: 25px;
  border: 2px solid transparent;
  background-color: #f4f4f8;
  color: #36454f;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.3s, background-color 0.3s;
}

.option-button:hover, .option:hover {
  background-color: #d0ccd3;
  transform: translateY(-2px);
}

.feedback, .feedback-correct, .feedback-incorrect {
  margin-top: 20px;
  font-weight: bold;
  padding: 5px 10px;
  border-radius: 15px;
  display: inline-block;
  color: #FFF;
  text-align: center;
}

.correct, .feedback-correct {
  background-color: #4CAF50;
  border: 5px solid #FFF;
  
}

.incorrect, .feedback-incorrect {
  background-color: #F44336;
  border: 5px solid #FFF;
}

.navigation-buttons, .button-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.navigation-button, .action-button, .restart-quiz-button, .hint-button {
  background-color: #36454f;
  color: white;
  border: none;
  border-radius: 25px;
  padding: 10px 20px;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;
}

.navigation-button:hover, .action-button:hover, .restart-quiz-button:hover, .hint-button-hover {
  background-color: #4A148C;
}

.results-summary {
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
  border: 2px solid #DDD;
}

.results-summary h4, .centered-title {
  color: #333;
  font-size: 1.5rem;
}

.results-summary p {
  color: #555;
  font-size: 1rem;
}

.option-card {
  background-color: #f4f4f8;
  border: 1px solid #ccc;
  padding: 10px;
  margin: 5px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color .3s, transform .2s;
}

.option-card:hover {
  background-color: #e9e9ef;
  transform: scale(1.02);
}

.feedback-icon {
  font-weight: bold;
  margin-left: 15px;
}

.quiz-container h1 {
  font-size: 2rem;
  color: #4A148C;
  margin-bottom: 15px;
  text-align: center;
}

</style>
